import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import NotificationTextCollection from './TextCollection';
import NotificationTextFile from './TextFile';
import NotificationTextImport from './TextImport';
import NotificationTextShare from './TextShare';
import NotificationTextUser from './TextUser';
import NotificationTextUploadPage from './TextUploadPage';

import { getNotificationDataText } from '../../helpers/Notifications';

const NotificationsText = ({ notification }) => {
  const intl = useIntl();

  const {
    type,
    object,
    user,
    data,
  } = notification;

  switch (type) {
    case 'CollectionFavorited': {
      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: 'favorited Collection' })}
          {' '}
          <NotificationTextCollection collection={object} />
        </span>
      );
    }

    case 'FileFavorited':
    case 'ShareFileFavorited': {
      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: 'favorited' })}{' '}
          <NotificationTextFile file={object} />
        </span>
      );
    }

    case 'FileNewVersionUploaded': {
      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: 'added new version of a file' })}
          {' '}
          <NotificationTextFile file={object} />
        </span>
      );
    }

    case 'CollectionFilesAdded': {
      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: 'added {notification} to Collection' }, {
            notification: getNotificationDataText(type, data, intl),
          })}
          {' '}
          <NotificationTextCollection collection={object} />
        </span>
      );
    }

    case 'CollectionAccessGranted': {
      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: 'invited you to collaborate on a Collection' })}
          {' '}
          <NotificationTextCollection collection={object} />
        </span>
      );
    }

    case 'CommentPosted':
    case 'ShareCommentPosted': {
      if (!user.uuid && data.user_name) {
        user.name = data.user_name;
      }

      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: '{notification} on' }, {
            notification: getNotificationDataText(type, data, intl),
          })}
          {' '}
          <NotificationTextFile file={object} url={data.url} />
        </span>
      );
    }

    case 'CommentMentioned':
    case 'ShareCommentMentioned': {
      if (!user.uuid && data.user_name) {
        user.name = data.user_name;
      }

      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({ defaultMessage: '{notification} on' }, {
            notification: getNotificationDataText(type, data, intl),
          })}
          {' '}
          <NotificationTextFile file={object} url={data.url} />
        </span>
      );
    }

    case 'ShareViewed': {
      return (
        <span>
          {intl.formatMessage({ defaultMessage: 'Someone viewed' })}
          {' '}
          <NotificationTextShare share={object} />{' '}
          {intl.formatMessage({ defaultMessage: 'for the first time' })}
        </span>
      );
    }

    case 'ImportFinished': {
      return (
        <span>
          <NotificationTextImport importObject={object} />
        </span>
      );
    }

    case 'UploadPageUpload': {
      if (!user.uuid && data.user_name) {
        user.name = data.user_name;
      }

      return (
        <span>
          <NotificationTextUser id={notification.id} user={user} />
          {intl.formatMessage({
            defaultMessage: 'uploaded {files} {files, plural, one {file} other {files}}',
          }, {
            files: data.files,
          })}
          {object?.collection && (
            <>
              {' '}
              {intl.formatMessage({ defaultMessage: 'to Collection' })}
              {' '}
              <NotificationTextCollection collection={object.collection} />
            </>
          )}
          {' '}
          {intl.formatMessage({ defaultMessage: 'via' })}
          {' '}
          <NotificationTextUploadPage uploadPage={object} url={object?.url?.full} />
        </span>
      );
    }

    default:
      return null;
  }
};

NotificationsText.propTypes = {
  notification: PropTypes.object.isRequired,
};

export default NotificationsText;
